'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ShieldAlert } from 'lucide-react';

type SessionEndedOverlayProps = {
  message?: string;
  onSignIn?: () => void;
};

export default function SessionEndedOverlay({ message, onSignIn }: SessionEndedOverlayProps) {
  const pathname = usePathname();
  const signInHref = `/login?next=${encodeURIComponent(pathname)}`;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.72)', backdropFilter: 'blur(6px)' }}
    >
      <div
        className="w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
        style={{
          background: '#ffffff',
          border: '1px solid rgba(0,180,160,0.22)',
        }}
        role="alertdialog"
        aria-labelledby="session-ended-title"
        aria-describedby="session-ended-desc"
      >
        {/* Top accent bar */}
        <div
          className="h-1 w-full"
          style={{ background: 'linear-gradient(90deg,#00b4a0,#1B3A6B)' }}
        />
        <div className="p-8">
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(0,180,160,0.1)', border: '1px solid rgba(0,180,160,0.2)' }}
            >
              <ShieldAlert size={18} style={{ color: '#00b4a0' }} />
            </div>
            <h2 id="session-ended-title" className="text-lg font-bold" style={{ color: '#0F2041' }}>
              Session ended
            </h2>
          </div>
          <p
            id="session-ended-desc"
            className="text-sm leading-relaxed mb-6"
            style={{ color: '#475569' }}
          >
            {message || 'Please sign in again to protect your private vault records.'}
          </p>
          <div className="flex flex-col gap-2.5">
            <Link
              href={signInHref}
              onClick={onSignIn}
              className="w-full inline-flex items-center justify-center py-3 rounded-xl font-bold text-sm text-white transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(135deg,#1B3A6B,#0d9488)' }}
            >
              Sign in again
            </Link>
            <Link
              href="/"
              className="w-full inline-flex items-center justify-center py-3 rounded-xl font-bold text-sm border-2 transition-colors hover:bg-slate-50"
              style={{ color: '#1B3A6B', borderColor: '#1B3A6B' }}
            >
              Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
